<template>
  <div class="request-editor py-2 px-2 my-1">
    <div v-if="closable" class="d-flex align-center">
      <v-spacer />
      <span v-if="request.code" class="body-2 font-weight-medium mr-2">{{ request.code }}</span>
      <quotation-icon-button icon="mdi-check" color="primary" :tooltip="$t('quotations.calc.done_editing')" :disabled="saving || !valid" @click="$emit('done')" />
      <quotation-icon-button icon="mdi-close" :tooltip="$t('cancel')" :disabled="saving" @click="$emit('cancel')" />
    </div>
    <v-row dense class="mt-1">
      <v-col cols="12" md="6">
        <v-autocomplete
          :value="request.partyId"
          :items="parties"
          item-text="companyName"
          item-value="id"
          :label="`${$t('quotations.recipients.party')} *`"
          :disabled="saving"
          outlined
          dense
          hide-details
          @change="setParty"
        >
          <template v-slot:prepend-inner><v-icon small class="mt-1">mdi-domain</v-icon></template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" md="6">
        <v-autocomplete
          v-model="request.personId"
          :items="partyPersons"
          :item-text="personName"
          item-value="id"
          :label="$t('quotations.recipients.person')"
          :disabled="saving || !request.partyId"
          :no-data-text="$t('quotations.recipients.no_persons')"
          outlined
          dense
          hide-details
          clearable
        >
          <template v-slot:prepend-inner><v-icon small class="mt-1">mdi-account-outline</v-icon></template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" md="6">
        <date-picker
          :date="request.receivedDate"
          :label="$t('quotations.recipients.received')"
          :saving="saving"
          :input-styles="{ outlined: true, dense: true }"
          prepend-inner-icon="mdi-calendar"
          clearable
          @change="v => $set(request, 'receivedDate', v)"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-select v-model="request.channel" :items="channelOptions" :label="$t('quotations.recipients.channel')" :disabled="saving" outlined dense hide-details />
      </v-col>
      <v-col cols="12" md="6">
        <v-row dense>
          <v-col cols="7">
            <date-picker
              :date="request.deadline"
              :label="`${$t('quotations.recipients.deadline')} *`"
              :saving="saving"
              :input-styles="{ outlined: true, dense: true }"
              prepend-inner-icon="mdi-calendar"
              clearable
              @change="v => $set(request, 'deadline', v)"
            />
          </v-col>
          <v-col cols="5">
            <time-picker
              :time="request.deadlineTime"
              :label="$t('quotations.recipients.deadline_time')"
              :saving="saving"
              :input-styles="{ outlined: true, dense: true }"
              prepend-inner-icon="mdi-clock-outline"
              clearable
              @change="v => $set(request, 'deadlineTime', v)"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field v-model="request.source" :label="$t('quotations.recipients.source')" :disabled="saving" outlined dense hide-details />
      </v-col>
      <v-col cols="12">
        <quotation-request-dates :dates="request.dates || []" :saving="saving" editable @change="v => $set(request, 'dates', v)" />
      </v-col>
      <v-col cols="12">
        <v-textarea v-model="request.note" :label="$t('quotations.recipients.note')" :disabled="saving" outlined dense auto-grow rows="1" hide-details />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import DatePicker from '@/components/DatePicker';
import TimePicker from '@/components/TimePicker';
import QuotationIconButton from './QuotationIconButton';
import QuotationRequestDates from './QuotationRequestDates';

export default {
  name: 'QuotationRequestEditor',
  components: { DatePicker, TimePicker, QuotationIconButton, QuotationRequestDates },
  props: {
    request: { type: Object, required: true },
    parties: { type: Array, default: () => [] },
    persons: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
    // Tallennetun tarjouksen muokkain suljetaan valmis- tai peruutusnapista; uudella tarjouksella se on aina auki
    closable: { type: Boolean, default: false },
  },
  data() {
    return {
      channelOptions: ['email', 'phone', 'portal', 'other'].map(c => ({ text: this.$t(`quotations.basics.channels.${c}`), value: c })),
    };
  },
  computed: {
    // Tilaaja ja jättöpäivä ovat pakollisia, ja tärkeällä päivällä pitää olla kuvaus ja päivämäärä
    valid() {
      const r = this.request;
      return !!r.partyId && !!r.deadline && (r.dates || []).every(d => d.title && d.title.trim() && d.dueDate);
    },
    partyPersons() {
      return this.persons.filter(p => p.employer && p.employer.id === this.request.partyId);
    },
  },
  methods: {
    personName(p) {
      return `${p.firstname} ${p.lastname}`;
    },
    setParty(partyId) {
      this.$set(this.request, 'partyId', partyId);
      this.$set(this.request, 'personId', null);
    },
  },
};
</script>

<style scoped>
.request-editor {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
}
</style>
